import { useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { CONNECTOR_LABEL, useConnection } from '@/entities/connection';
import { datasetTone } from '@/entities/dataset';
import { useBindings } from '@/features/rules';
import { useCursorStack } from '@/shared/api/paging';
import { formatInstant } from '@/shared/model/instant';
import { SCREEN_PATHS } from '@/shared/routes';
import { Alert } from '@/shared/ui/Alert';
import { Card } from '@/shared/ui/Card';
import { DataTable, PrimaryCell } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { TextField } from '@/shared/ui/Field';
import { SkeletonRows } from '@/shared/ui/Skeleton';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { Tabs } from '@/shared/ui/Tabs';
import { Toolbar } from '@/shared/ui/Toolbar';

import { DATASETS_PER_PAGE, usePagedDatasets } from '../api/catalog';
import { useConnectionSummaries, useDatasetFacts } from '../api/catalogue';
import { useLatestScores } from '../api/scores';
import { bindingCount, profileReading, scoredCoverage } from '../model/catalogue';
import { nameGlobMatches } from '../model/selectors';
import { BulkAnalysisControl } from './BulkAnalysisControl';
import { PiiTable, ProfilingTable } from './DatasetAnalysisTables';
import { ProfileCell } from './ProfileCell';
import { ScoreCell } from './ScoreCell';

/* One source's catalogue: the tables a harvest of it discovered.
 *
 * **The middle level of the drill-down, and a table rather than cards.** The level above is a
 * handful of sources each with a state and a next step, which is a card's question. This is up to
 * five thousand tables compared on the same axes — rows, score, when it was last profiled — which is
 * exactly what a dense table is for.
 *
 * **`ProfileCell` and `ScoreCell` are the two columns that must not lie**, and both are built the
 * same way: three or four distinguishable absences rather than a zero. A table that showed `0 rows`
 * for an unprofiled dataset would report every unmeasured table as empty, on the screen a steward
 * uses to decide what to measure next.
 *
 * **Deliberately not `CatalogBrowser` with a `connectionId`.** That component exists and is still
 * used by the connection's own settings screen, and it answers a different question: it is the
 * *catalogue* browser, with PII and version columns, grouped by source. This one answers "what is in
 * this database and how much of it have we measured", which is a different column set — and one
 * table serving both would need a flag deciding which columns to show, which is how a screen ends up
 * rendering a PII column with nothing in it.
 */

function Breadcrumbs({ name }: { readonly name: string | null }): ReactElement {
  const { t } = useTranslation();
  return (
    <nav aria-label={t('breadcrumbs.label')} className="lq-breadcrumbs">
      <Link to={SCREEN_PATHS.datasets}>{t('catalogue.crumb.allSources')}</Link>
      <span aria-hidden="true" className="lq-breadcrumbs__sep">
        /
      </span>
      {/* `aria-current="page"` rather than a link to here — the choice both other breadcrumbs in
          this app make, because a last crumb that navigates to the page you are on costs a
          screen-reader user a round trip for nothing. */}
      <span aria-current="page">{name ?? t('catalogue.source.unlisted')}</span>
    </nav>
  );
}

export function SourceDatasets(): ReactElement {
  const { t } = useTranslation();
  const { connectionId = '' } = useParams<{ connectionId: string }>();

  const connection = useConnection(connectionId);
  const summaries = useConnectionSummaries();
  const [search, setSearch] = useState('');
  /* Which of the three views is showing. **Local state, not a route**, matching `DatasetDetail`'s
     own tab strip: these are three renderings of one page's data rather than three addresses, and
     nothing links to a particular one. `datasets` is the default, so the screen a reader already
     knows is the screen they still get. */
  const [view, setView] = useState<'datasets' | 'profiling' | 'pii'>('datasets');
  const pager = useCursorStack();
  const query = useMemo(() => ({ search, connectionId }), [search, connectionId]);
  const datasets = usePagedDatasets(query, pager.cursor);

  const datasetIds = useMemo(
    () => (datasets.data?.items ?? []).map((each) => each.id),
    [datasets.data],
  );
  const scores = useLatestScores(datasetIds);
  /* One request for the page's profile facts rather than one per row. Both this and the scores read
     render as honest blanks while in flight, so the table appears as soon as the catalogue does. */
  const facts = useDatasetFacts(datasetIds);
  /* Enabled bindings, once for the page. The count per row is a *lower bound* matched on
     `name_glob` — see `bindingCount` — and it is the same approximation `DatasetDetail`'s rules
     panel already ships rather than a second answer to "which rules cover this table". */
  const bindings = useBindings();

  const summary = summaries.data?.items.find(
    (each) => each.connection_id === connectionId,
  );
  const coverage = summary === undefined ? null : scoredCoverage(summary);

  function searchFor(next: string): void {
    setSearch(next);
    /* Required rather than tidy: a cursor is signed over a fingerprint of the query it was issued
       for, so carrying one across a search change is refused with a 400. */
    pager.reset();
  }

  return (
    <section aria-labelledby="source-datasets-heading">
      <Breadcrumbs name={connection.data?.name ?? null} />

      {/* **No action beside the title, deliberately.** A link to the connection's own screen was
          here and is gone: this page is reached *from* a source card and its breadcrumb already
          leads back, so a second door to a neighbouring screen is a control competing with the
          table for a reader who came here to read it. Verify, harvest and retire live on
          Connections, which is one click away through the crumb. */}
      <h2 id="source-datasets-heading" className="lq-pagetitle__text lq-mono">
        {connection.data?.name ?? t('catalogue.source.unlisted')}
      </h2>

      {/* The one-line summary the reference puts under the title: how much is here, how much is
          measured, how current it is. Assembled from the same summary read the cards used, so the
          two screens cannot disagree — and each clause is omitted rather than guessed when its
          figure is absent. */}
      <p className="lq-pagetitle__lede">
        {summary === undefined ? (
          <span className="lq-dim">
            {summaries.isError
              ? t('catalogue.source.summaryUnread')
              : t('catalogue.source.summaryLoading')}
          </span>
        ) : (
          [
            t('catalogue.source.datasetCount', { count: summary.datasets }),
            coverage?.kind === 'measured'
              ? t('catalogue.source.scoredCount', { count: coverage.covered })
              : null,
            summary.last_harvest_at === null
              ? t('catalogue.source.neverHarvested')
              : t('catalogue.source.harvestedAgo', {
                  at: formatInstant(summary.last_harvest_at) ?? summary.last_harvest_at,
                }),
            connection.data !== undefined
              ? CONNECTOR_LABEL[connection.data.connector_type]
              : null,
          ]
            .filter((clause): clause is string => clause !== null)
            .join(' · ')
        )}
      </p>

      {datasets.isError ? (
        <Alert tone="error" title={t('catalog.unreachable')}>
          {datasets.error.message}
        </Alert>
      ) : null}

      <Toolbar
        count={
          datasets.isSuccess
            ? t('catalog.pageCount', { count: datasets.data.items.length })
            : undefined
        }
      >
        <TextField
          label={t('catalog.searchLabel')}
          type="search"
          value={search}
          onChange={searchFor}
          placeholder={t('catalog.searchPlaceholder')}
        />
        {/* Immediately right of the search, on the same row, which is where the toolbar puts its
            controls. Both run against the whole source rather than the page on screen — and
            against the *search*, so a reader who has filtered gets the datasets they can see.
            `empty` disables them rather than letting a press explain itself, so a source with
            nothing in it makes no request at all. See `BulkAnalysisControl`. */}
        <BulkAnalysisControl
          connectionId={connectionId}
          search={search}
          empty={datasets.isSuccess && datasets.data.items.length === 0}
        />
      </Toolbar>

      {/* **Always present, not revealed by a bulk run.** Both views render state the backend
          persists — `last_profiled_at` and `classified` — so they are just as true for a source
          profiled last week from `lumiq-cli` as for one this screen just ran. Showing them only
          after a run would hide the answer from the reader most likely to want it: the one deciding
          whether to run anything at all. */}
      <Tabs
        label={t('catalogue.tabs.label')}
        active={view}
        onSelect={setView}
        tabs={[
          {
            id: 'datasets',
            label: t('catalogue.tab.datasets'),
            ...(datasets.isSuccess ? { count: datasets.data.items.length } : {}),
          },
          { id: 'profiling', label: t('catalogue.tab.profiling') },
          { id: 'pii', label: t('catalogue.tab.pii') },
        ]}
      />

      {datasets.isPending ? (
        <>
          <p role="status" className="lq-muted">
            {t('catalog.loading')}
          </p>
          <SkeletonRows rows={8} columns={6} />
        </>
      ) : null}

      {/* The empty state is the *fetch's* answer rather than a view's, so it shows on whichever
          tab is open: a source with no datasets has none to profile and none to classify. */}
      {datasets.isSuccess &&
        (datasets.data.items.length === 0 ? (
          <EmptyState
            title={
              search.trim().length > 0
                ? t('catalog.noMatches')
                : t('catalogue.source.noDatasetsTitle')
            }
            guidance={
              search.trim().length > 0
                ? t('catalog.noMatchesGuidance', { search: search.trim() })
                : t('catalogue.source.noDatasetsGuidance')
            }
          />
        ) : view !== 'datasets' ? null : (
          <Card flush>
            <DataTable
              caption={t('catalogue.source.tableCaption')}
              columns={[
                { key: 'name', label: t('catalog.column.name') },
                /* Rows before score, matching the reference and the reading order of the
                   question: how big is this table, then how good is it. */
                { key: 'rows', label: t('catalogue.column.rows'), numeric: true },
                { key: 'score', label: t('catalog.column.score') },
                {
                  key: 'rules',
                  label: t('catalogue.column.rules'),
                  numeric: true,
                },
                { key: 'state', label: t('catalog.column.state') },
                { key: 'profiled', label: t('catalogue.column.lastProfiled') },
              ]}
            >
              {datasets.data.items.map((dataset) => {
                const reading = profileReading(facts.data?.get(dataset.id), {
                  /* Loading is not "unread": while the request is in flight the honest answer is
                     that we do not know yet, and `facts.data` is undefined so every row would
                     otherwise read as never-profiled. Only a *failed* read is unread. */
                  unread: facts.isError,
                });
                return (
                  <tr key={dataset.id}>
                    <td>
                      <PrimaryCell>
                        <Link
                          className="lq-cell__primary"
                          to={`${SCREEN_PATHS.datasets}/${dataset.id}`}
                        >
                          <span className="lq-cell__prefix">{dataset.schema_name}.</span>
                          {dataset.object_name}
                        </Link>
                      </PrimaryCell>
                    </td>
                    <td className="lq-table__num lumiq-numeric">
                      <ProfileCell
                        reading={reading}
                        show="rows"
                        pending={facts.isPending}
                      />
                    </td>
                    <td>
                      <ScoreCell
                        summary={scores.data?.get(dataset.id)}
                        unavailable={scores.isError}
                      />
                    </td>
                    <td className="lq-table__num lumiq-numeric">
                      {/* A lower bound, said as one. `≥ 3` rather than `3 bound`, because the
                          figure misses a binding that reaches this table by tag or column role and
                          a bare number would read as the whole truth. */}
                      {(() => {
                        const rules = bindingCount(
                          bindings.data?.items,
                          `${dataset.schema_name}.${dataset.object_name}`,
                          nameGlobMatches,
                        );
                        if (rules.kind === 'unread') {
                          return (
                            <span
                              className="lq-muted"
                              title={t('catalogue.rules.unread.meaning')}
                            >
                              {t('catalogue.rules.unread.label')}
                            </span>
                          );
                        }
                        return rules.count === 0 ? (
                          <span
                            className="lq-muted"
                            title={t('catalogue.rules.none.meaning')}
                          >
                            {t('catalogue.rules.none.label')}
                          </span>
                        ) : (
                          <span title={t('catalogue.rules.atLeast.meaning')}>
                            {t('catalogue.rules.atLeast.label', { count: rules.count })}
                          </span>
                        );
                      })()}
                    </td>
                    <td>
                      <StatusBadge
                        tone={datasetTone(dataset)}
                        label={t(`catalog.state.${dataset.state}.label`)}
                        title={t(`catalog.state.${dataset.state}.meaning`)}
                      />
                    </td>
                    <td>
                      <ProfileCell
                        reading={reading}
                        show="when"
                        pending={facts.isPending}
                      />
                    </td>
                  </tr>
                );
              })}
            </DataTable>
          </Card>
        ))}

      {/* **The same rows and the same facts the table above uses**, so switching tabs issues no
          request and the three views cannot describe different things. Both delegate their
          verdicts to `ProfileCell` and `PiiCell` — see `DatasetAnalysisTables` for why
          re-deriving either here would be the one duplication that matters. */}
      {datasets.isSuccess && datasets.data.items.length > 0 && view === 'profiling' ? (
        <ProfilingTable
          rows={datasets.data.items}
          facts={facts.data}
          factsPending={facts.isPending}
          factsUnread={facts.isError}
        />
      ) : null}

      {datasets.isSuccess && datasets.data.items.length > 0 && view === 'pii' ? (
        <PiiTable rows={datasets.data.items} />
      ) : null}

      {datasets.isSuccess && datasets.data.items.length > 0 ? (
        <>
          <div className="lq-pager">
            {/* The page number first and the controls pushed right, which is the reference's order.
                A spacer rather than `space-between`, so the label stays hard left when the controls
                wrap under it. */}
            <span>{t('catalog.pager.page', { page: pager.page })}</span>
            <span className="lq-pager__spacer" />
            <button
              type="button"
              className="lq-pager__btn"
              disabled={!pager.canGoBack}
              onClick={pager.back}
            >
              {t('catalog.pager.back')}
            </button>
            {/* Ghost, like Back. The reference gives Next no accent: paging forward is how you read a
                listing rather than a decision about it, and an accent-filled control would be the
                loudest thing on a page whose subject is the rows. */}
            <button
              type="button"
              className="lq-pager__btn"
              disabled={datasets.data.nextCursor == null}
              onClick={() => {
                pager.forward(datasets.data.nextCursor);
              }}
            >
              {t('catalog.pager.next')}
            </button>
          </div>
          {datasets.data.items.length === DATASETS_PER_PAGE &&
          datasets.data.nextCursor == null ? (
            <p className="lq-muted">{t('catalog.pager.exactlyFull')}</p>
          ) : null}
        </>
      ) : null}

      {/* The footnote the reference carries, and it is the table's own honesty note rather than
          decoration: two of these five columns are figures from the *last* harvest and the *last*
          profile, and a reader comparing them across rows should know they were not all taken at
          the same moment. */}
      {datasets.isSuccess && datasets.data.items.length > 0 ? (
        <p className="lq-muted lq-table__footnote">{t('catalogue.source.footnote')}</p>
      ) : null}
    </section>
  );
}
